<script setup lang="ts">
  import {
    Color,
    LineHeight,
    Link,
    RequiredExtensions,
    TextAlign,
    TextBlocks,
    TextSize,
    UIRichTextEditor,
  } from '@gohighlevel/ghl-text-editor'
  import TextStyle from '@tiptap/extension-text-style'
  import Underline from '@tiptap/extension-underline'
  import BulletList from '@tiptap/extension-bullet-list'
  import StarterKit from '@tiptap/starter-kit'
  import ListItem from '@tiptap/extension-list-item'
  import HardBreak from '@tiptap/extension-hard-break'
  import { Editor } from '@tiptap/vue-3'
  import { PropType, computed, ref, watch, provide, onMounted, defineExpose } from 'vue'
  import {
    MagicPlaceholderExtension,
    ELEMENTS_META,
    ExtendedFontFamily,
    IElement,
    Storage,
    CustomLineHeight,
    extractFontFamiliesFromJSON,
    FontFamilyV2,
    ExtendedHeading,
    InsertGapOnPaste,
  } from '@gohighlevel/ghl-proposals-common'
  import { EditorState } from '@tiptap/pm/state'
  import { useElementOption } from '@/composable/elementOptions'
  import { useDocument } from '@/composition'
  import { useBuilderStore } from '@/store/builder'

  const editor = ref<Editor>()
  const store = useBuilderStore()
  const { updateOption } = useElementOption()
  const { loadingCustomValues, documentVersion } = useDocument()

  const props = defineProps({
    genKey: { type: String },
    element: {
      type: Object as PropType<IElement>,
    },
    customValues: {
      type: Object as PropType<Record<string, string>>,
      required: true,
    },
  })
  const isEditable = computed(() => store.isDocumentEditable)

  const CustomBulletList = BulletList.extend({
    addCommands() {
      return {
        ...this.parent?.(),
        toggleCustomBulletList: () => editor => {
          return editor.commands.toggleBulletList()
        },
      }
    },
  })

  const HardBreakExtension = HardBreak.extend({
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          if (this.editor.isActive('orderedList') || this.editor.isActive('bulletList')) {
            return this.editor.chain().createParagraphNear().run()
          }
          return this.editor.commands.setHardBreak()
        },
      }
    },
  })

  editor.value = new Editor({
    content: props.element?.component.options.text,
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            class: 'break-inside-auto', // Your custom class name
          },
        },
      }),
      MagicPlaceholderExtension.configure({
        placeholderValues: props.customValues,
        loading: loadingCustomValues.value,
      }),
      TextSize,
      RequiredExtensions,
      TextStyle,
      ...((documentVersion.value ?? 1) === 1 ? [ExtendedFontFamily] : []),
      ...((documentVersion.value ?? 1) > 1 ? [FontFamilyV2] : []),
      TextBlocks,
      ...((documentVersion.value ?? 1) > 1 ? [ExtendedHeading] : []), // dont change the order, this should be added after TextBlocks
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        protocols: ['ftp', 'mailto', '#popup', 'sms', 'tel', '{{', '/'],
        autolink: true,
        linkOnPaste: false,
      }),
      // Replace the LineHeight extension with CustomLineHeight
      ...((documentVersion.value ?? 1) > 1
        ? [
            CustomLineHeight.configure({
              defaultLineHeight: 1.5,
              types: ['paragraph', 'heading', 'blockquote', 'listItem'],
            }),
          ]
        : []),
      ...((documentVersion.value ?? 1) === 1 ? [LineHeight] : []),
      ListItem,
      CustomBulletList,
      ...((documentVersion.value ?? 1) === 1
        ? [
            HardBreakExtension.configure({
              HTMLAttributes: {
                class: 'break-after-auto page-break',
              },
            }),
          ]
        : []),
      Storage.configure({
        storage: {
          version: props.element?.version ?? 1,
          documentVersion: documentVersion.value ?? 1,
        },
      }),
      ...((documentVersion.value ?? 1) > 1 ? [InsertGapOnPaste] : []),
    ],
    autofocus: false,
    editable: isEditable.value,
    onUpdate({ editor }) {
      updateOption(ELEMENTS_META.TEXT, editor.getHTML(), props?.element?.id, props?.element?.type)
    },
    parseOptions: {
      preserveWhitespace: true,
    },
    editorProps: {
      attributes: (state: EditorState) => {
        return {
          ...state.doc.attrs,
          ...((documentVersion.value ?? 1) === 1 ? { style: 'font-family: sans-serif' } : {}),
          ...(documentVersion.value > 1 ? { style: 'font-family: Open Sans' } : {}),
        }
      },
      handleClick: () => {
        updateLinkState()
        return false // Let other click handlers run
      },
    },
  })
  watch(isEditable, () => {
    editor.value?.setEditable(isEditable.value)
  })

  // eslint-disable-next-line listeners/no-inline-function-event-listener
  editor.value.on('focus', () => {
    store.updateEditorInstance(editor.value as unknown as typeof Editor)
    updateLinkState()
  })
  editor.value.off('focus', () => {
    store.updateEditorInstance(editor.value as unknown as typeof Editor)
  })

  watch(
    () => store.moveElementDragState,
    val => {
      editor.value?.setEditable(!val)
    }
  )

  // Only for undo/redo operation to work since tiptap editor does not refresh on updating pages state
  watch(
    () => store.updateEditors,
    () => {
      editor.value?.commands.setContent(props.element?.component.options.text)
    },
    { deep: true }
  )

  // Add a ref to store link state
  const currentLinkState = ref<any>(null)

  // Add a function to update link state
  const updateLinkState = () => {
    if (editor.value) {
      const attrs = editor.value.getAttributes('link')
      if (attrs?.href) {
        currentLinkState.value = attrs
        // Provide the link state to child components
        provide('rte-link-state', attrs)
      }
    }
  }

  // Add watcher for link state changes
  watch(currentLinkState, newState => {
    if (newState?.href) {
      // We're using provide/inject for state management
      provide('rte-link-state', newState)
    }
  })

  // Also update link state when selection changes
  // eslint-disable-next-line listeners/no-inline-function-event-listener, listeners/no-missing-remove-event-listener
  editor.value.on('selectionUpdate', () => {
    updateLinkState()
  })

  // Export currentLinkState for use in the modal
  defineExpose({
    currentLinkState,
  })

  onMounted(() => {
    if (documentVersion.value > 1) {
      // eslint-disable-next-line listeners/no-inline-function-event-listener, listeners/no-missing-remove-event-listener
      editor.value?.on('blur', () => {
        const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
        store.setLoadedFontsFromEditor(Array.from(fonts))
      })
      // eslint-disable-next-line listeners/no-inline-function-event-listener, listeners/no-missing-remove-event-listener
      editor.value?.on('create', () => {
        const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
        store.setLoadedFontsFromEditor(Array.from(fonts))
      })
    }
  })
</script>

<template>
  <UIRichTextEditor :editor="editor" />
</template>
